// An activity is basics plus modules (redesign §2): the modules the organizer added are kept in
// events.additional_settings.modules, never derived from data. The menu, the "Toevoegen" page and
// removing a module all read this.
export type EventModule =
  | 'timeslots' | 'seatmap'
  | 'discounts' | 'questions' | 'theme'
  | 'waitlist' | 'queue'
  | 'mails' | 'ticket_design' | 'self_service' | 'thank_you_page'
  | 'tracking';

// Per step of the buyer, in menu order. Donation is not a module of its own: it is a question type.
export const MODULE_GROUPS: { key: string; modules: EventModule[] }[] = [
  { key: 'tickets', modules: ['timeslots', 'seatmap'] },
  { key: 'shop', modules: ['discounts', 'questions', 'theme'] },
  { key: 'busy', modules: ['waitlist', 'queue'] },
  { key: 'after', modules: ['mails', 'ticket_design', 'self_service', 'thank_you_page'] },
  { key: 'measure', modules: ['tracking'] },
];
export const EVENT_MODULES: EventModule[] = MODULE_GROUPS.flatMap((g) => g.modules);

export const MODULE_ICONS: Record<EventModule, string> = {
  timeslots: 'clock', seatmap: 'chair', discounts: 'tag', questions: 'clipboard-question', theme: 'palette',
  waitlist: 'hourglass-half', queue: 'users-line', mails: 'envelope', ticket_design: 'file-pdf',
  self_service: 'hand', thank_you_page: 'arrow-up-right-from-square', tracking: 'chart-line',
};

type EventLike = {
  id: string; additional_settings?: unknown; seating_plan_id?: string | null; shop_theme_id?: string | null;
  ticket_design_id?: string | null; event_timeslots: unknown[];
  discounts?: { count: number }[]; event_metadata_fields?: { count: number }[];
};

/** The page a module lives on today (the routes did not move). */
export const modulePath = (event: EventLike, key: EventModule, canReadOrders = true) => {
  const base = `/events/${event.id}/`;
  switch (key) {
    case 'timeslots': return base + 'agenda';
    // the seat map page needs a plan; until then its picker in Meer instellingen
    case 'seatmap': return base + (event.seating_plan_id ? 'seats' : 'settings/more?open=seatmap');
    case 'discounts': return base + 'discounts';
    case 'questions': return base + 'settings/metadata';
    case 'theme': return base + 'settings/theme';
    case 'waitlist': return base + (canReadOrders ? 'waitlist' : 'settings/waitlist');
    case 'mails': return base + 'mails';
    case 'ticket_design': return base + 'ticket-design';
    case 'self_service': return base + 'settings/self-service';
    case 'tracking': return base + 'settings/tracking';
    // these two are dialogs on Meer instellingen
    case 'queue': case 'thank_you_page': return base + 'settings/more?open=' + key;
  }
};

/** The kept list, in menu order; null when the activity has none yet. */
export const keptModules = (event: { additional_settings?: unknown } | null | undefined): EventModule[] | null => {
  const list = (event?.additional_settings as { modules?: unknown } | null)?.modules;
  return Array.isArray(list) ? EVENT_MODULES.filter((k) => list.includes(k)) : null;
};

/** additional_settings with this list in it, in menu order. */
export const withModules = (settings: unknown, list: EventModule[]) =>
  ({ ...(settings as Record<string, unknown> | null), modules: EVENT_MODULES.filter((k) => list.includes(k)) });

/**
 * What an activity without a kept list uses, once: the same conditions as the backfill migration
 * (20261103110000_event_modules), plus the organization's default (shop_settings.modules). Only for
 * an activity made after that migration; the store keeps the result, so it is never derived again.
 */
export const modulesInUse = (
  event: EventLike,
  org: { shop_settings?: unknown; tracking?: unknown } | null | undefined,
  mailsReach: boolean,
): EventModule[] => {
  const s = (event.additional_settings ?? {}) as Record<string, unknown>;
  const own = (org?.shop_settings as { modules?: unknown } | null)?.modules;
  const used: Record<EventModule, boolean> = {
    timeslots: event.event_timeslots.length !== 1,
    seatmap: !!event.seating_plan_id,
    discounts: !!event.discounts?.[0]?.count,
    questions: !!event.event_metadata_fields?.[0]?.count,
    theme: !!event.shop_theme_id,
    waitlist: waitlistSettingsOf(event).enabled,
    queue: !!s.queue && typeof s.queue === 'object',
    mails: mailsReach,
    ticket_design: !!event.ticket_design_id,
    self_service: selfServiceOf((org?.shop_settings as { self_service?: unknown } | null)?.self_service, s.self_service).enabled,
    thank_you_page: typeof s.thank_you_page_url === 'string' && s.thank_you_page_url !== '',
    // the activity's own id wins, an empty one switches the organization's off
    tracking: INTEGRATION_KEYS.some((key) => !!integrationValues(INTEGRATIONS[key].idKey in s ? s : org?.tracking, key).id),
  };
  return EVENT_MODULES.filter((k) => used[k] || (Array.isArray(own) && own.includes(k)));
};
